/* The dashboard's visual vocabulary, taken from `portable/app/CodeNerveApp.jsx`.

   The prototype hand-rolls its furniture inline: a 14px panel with a mono uppercase
   caption over a hairline of `--surface-elevated`, a table whose header row sits on that
   same elevated surface, a dashed well for an empty panel, a filter pill that is a dot, a
   label and a chevron. Those are the design; they are reproduced here rather than
   approximated, because approximating a design system is how a product ends up with two.

   What is *not* carried over is the prototype's data. Every component below takes props.
   The prototype's arrays -- five memories, five pull requests, three members -- are the
   thing this application replaces.

   The props are this app's, not the prototype's: `checked`/`onChange` rather than
   `on`/`onClick`, because the screens are wired to an API and a controlled component is
   what that needs. The pixels are the prototype's. */

import { Search } from "lucide-react";

import githubMark from "@ds/assets/logos/mark-github.png";
import { useEffect, useState } from "react";

/* ------------------------------------------------------------------ panel furniture */

export const panel = {
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "14px",
};

/* Uppercase mono at 0.07em. The dashboard's section headings are labels, not titles. */
export const caption = {
  font: "var(--text-caption)",
  fontFamily: "var(--font-mono)",
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: "var(--text-muted)",
};

const HEAD = {
  display: "flex",
  alignItems: "center",
  gap: "10px",
  padding: "14px 18px",
  borderBottom: "1px solid var(--surface-elevated)",
};

/* The right-hand slot in a panel header is set in body type, not in the mono caption --
   it is a control, and reading it as a second heading would flatten the hierarchy. */
const HEAD_ASIDE = {
  marginLeft: "auto",
  display: "inline-flex",
  alignItems: "center",
  gap: "7px",
  font: "var(--text-body-14)",
  color: "var(--text-secondary)",
};

export function Panel({ title, action, children, style }) {
  return (
    <section style={{ ...panel, ...style }}>
      {(title || action) && (
        <header style={HEAD}>
          {title && <span style={caption}>{title}</span>}
          {action && <span style={HEAD_ASIDE}>{action}</span>}
        </header>
      )}
      {children}
    </section>
  );
}

/* Two panels side by side, each stretched to the taller of the pair so their dashed
   wells line up. The prototype clones its children to do this; a wrapper does it without
   reaching into anyone's props. */
export function PanelPair({ children }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit,minmax(330px,1fr))",
        gap: "clamp(14px,1.6vw,24px)",
      }}
    >
      {children}
    </div>
  );
}

export const column = { display: "flex", flexDirection: "column" };

export function Chevron() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ width: "12px", height: "12px" }}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export function LogoMark({ size = 26, color = "currentColor" }) {
  return (
    <svg
      viewBox="-100 -100 200 200"
      width={size}
      height={size}
      preserveAspectRatio="xMidYMid meet"
      aria-hidden="true"
      style={{ display: "block", flex: "0 0 auto" }}
    >
      <g fill={color} stroke={color}>
        <circle r="24" fill="none" strokeWidth="20" />
        <g strokeWidth="9" strokeLinecap="round">
          {MARK_SPOKES.map(([x1, y1, x2, y2]) => (
            <line key={`${x2}:${y2}`} x1={x1} y1={y1} x2={x2} y2={y2} />
          ))}
        </g>
        {MARK_SPOKES.map(([, , x, y]) => (
          <circle key={`d${x}:${y}`} cx={x} cy={y} r="7.5" stroke="none" />
        ))}
      </g>
    </svg>
  );
}

const MARK_SPOKES = [
  [34, 0, 85, 0],
  [29.44, 17, 73.61, 42.5],
  [17, 29.44, 42.5, 73.61],
  [0, 34, 0, 85],
  [-17, 29.44, -42.5, 73.61],
  [-29.44, 17, -73.61, 42.5],
  [-34, 0, -85, 0],
  [-29.44, -17, -73.61, -42.5],
  [-17, -29.44, -42.5, -73.61],
  [0, -34, 0, -85],
  [17, -29.44, 42.5, -73.61],
  [29.44, -17, 73.61, -42.5],
];

/* The glyph is black artwork, inverted for dark chrome -- the prototype's note. */
export function GitMark({ size = 14 }) {
  return (
    <img
      src={githubMark}
      alt=""
      aria-hidden="true"
      style={{
        width: `${size}px`,
        height: `${size}px`,
        objectFit: "contain",
        flex: "0 0 auto",
        filter: "brightness(0) invert(1)",
      }}
    />
  );
}

/* ------------------------------------------------------------------------- controls */

/* The prototype's primary control: a soft-white pill with charcoal ink. */
export const primaryButton = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  font: "var(--text-label)",
  color: "var(--btn-primary-ink)",
  background: "var(--btn-primary-bg)",
  border: "none",
  borderRadius: "9px",
  padding: "9px 14px",
};

export const secondaryButton = {
  display: "inline-flex",
  alignItems: "center",
  gap: "8px",
  cursor: "pointer",
  font: "var(--text-label)",
  color: "var(--text-secondary)",
  background: "var(--surface)",
  border: "1px solid var(--border)",
  borderRadius: "9px",
  padding: "9px 14px",
};

export const field = {
  background: "var(--surface-subtle)",
  border: "1px solid var(--border)",
  borderRadius: "9px",
  padding: "9px 12px",
  font: "var(--text-body-14)",
  color: "var(--text-primary)",
  outline: "none",
  minWidth: "180px",
};

export function Button({ tone = "primary", style, ...rest }) {
  const base = tone === "primary" ? primaryButton : secondaryButton;
  return (
    <button
      type="button"
      {...rest}
      style={{
        ...base,
        ...(rest.disabled ? { opacity: 0.45, cursor: "not-allowed" } : null),
        ...style,
      }}
    />
  );
}

/* A dot, a label and a chevron. The native select arrow is suppressed so the chevron the
   design draws is the only one. */
export function FilterPill({ label, value, options, onChange }) {
  const chosen = value !== null && value !== undefined && value !== "";
  return (
    <span
      style={{
        position: "relative",
        display: "inline-flex",
        alignItems: "center",
        gap: "9px",
        font: "var(--text-body-14)",
        color: "var(--text-secondary)",
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "9px",
        padding: "9px 13px",
        whiteSpace: "nowrap",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "999rem",
          background: chosen ? "var(--text-secondary)" : "var(--text-disabled)",
          flex: "0 0 auto",
        }}
      />
      <select
        aria-label={label}
        value={value ?? ""}
        onChange={(event) => onChange(event.target.value || null)}
        style={{
          appearance: "none",
          WebkitAppearance: "none",
          background: "transparent",
          border: "none",
          outline: "none",
          cursor: "pointer",
          font: "var(--text-body-14)",
          color: "var(--text-secondary)",
          padding: 0,
          paddingRight: "4px",
        }}
      >
        <option value="">{label}</option>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      <span aria-hidden="true" style={{ color: "var(--text-faint)" }}>
        <Chevron />
      </span>
    </span>
  );
}

export function SearchBox({ value, onChange, placeholder }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "9px",
        flex: 1,
        minWidth: "200px",
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "9px",
        padding: "9px 13px",
      }}
    >
      <Search size={14} color="var(--text-muted)" aria-hidden="true" />
      <input
        type="search"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        style={{
          flex: 1,
          minWidth: 0,
          background: "transparent",
          border: "none",
          outline: "none",
          font: "var(--text-body-14)",
          color: "var(--text-primary)",
        }}
      />
    </span>
  );
}

/* A search box that does not fire a request on every keystroke. */
export function useDebounced(value, delay = 300) {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return settled;
}

export function Toggle({ checked, onChange, label, disabled }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      style={{
        position: "relative",
        width: "42px",
        height: "24px",
        flex: "0 0 auto",
        borderRadius: "999rem",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.5 : 1,
        border: `1px solid ${checked ? "var(--control-on-border)" : "var(--border-strong)"}`,
        background: checked ? "var(--control-on-bg)" : "var(--surface-subtle)",
        transition: "background 260ms var(--ease-out), border-color 260ms var(--ease-out)",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "2px",
          left: checked ? "20px" : "2px",
          width: "18px",
          height: "18px",
          borderRadius: "999rem",
          background: checked ? "var(--control-on-ink)" : "var(--text-faint)",
          transition: "left 260ms var(--ease-out), background 260ms var(--ease-out)",
        }}
      />
    </button>
  );
}

/* A row of segmented buttons: the prototype's sub-tabs, its PR status filters, and its
   settings rail are all this shape. `counts` is optional and renders the mono tally the
   PR filters carry. */
export function Segments({ value, options, onChange, counts }) {
  return (
    <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
      {options.map(([key, label]) => {
        const on = value === key;
        return (
          <button
            key={key || "all"}
            type="button"
            onClick={() => onChange(key)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              font: "var(--text-body-14)",
              color: on ? "var(--text-primary)" : "var(--text-muted)",
              background: on ? "var(--surface-elevated)" : "transparent",
              border: `1px solid ${on ? "var(--border)" : "transparent"}`,
              borderRadius: "9px",
              padding: "8px 14px",
              transition: "background 240ms var(--ease-out), color 240ms var(--ease-out)",
            }}
          >
            {label}
            {counts && counts[key] !== undefined && (
              <span style={{ font: "10px/1 var(--font-mono)", color: "var(--text-muted)" }}>
                {counts[key]}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

/* ---------------------------------------------------------------------- data display */

/* A solid chip, not an outline: the prototype prints severity in the ink colour of a
   light control on a filled ground, so it reads at a glance in a dense table. */
export function Severity({ level }) {
  if (!level) return <span style={{ color: "var(--text-muted)" }}>—</span>;
  const tone =
    level === "P0" ? "var(--danger)" : level === "P1" ? "var(--warning)" : "var(--text-muted)";
  return (
    <span
      style={{
        font: "10px/1 var(--font-mono)",
        color: "var(--btn-primary-ink)",
        background: tone,
        padding: "4px 7px",
        borderRadius: "5px",
      }}
    >
      {level}
    </span>
  );
}

/* The mono uppercase chip the prototype uses for a memory's type and a member's role. */
export function Tag({ children }) {
  return (
    <span
      style={{
        font: "10px/1 var(--font-mono)",
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        color: "var(--text-secondary)",
        background: "var(--border)",
        padding: "5px 8px",
        borderRadius: "5px",
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </span>
  );
}

const STATE_TONE = {
  completed: "var(--success)",
  queued: "var(--warning)",
  waiting_for_index: "var(--warning)",
  failed: "var(--danger)",
  skipped: "var(--text-muted)",
  stale: "var(--text-muted)",
};

const STATE_LABEL = {
  completed: "Reviewed",
  queued: "In review",
  waiting_for_index: "Indexing",
  failed: "Failed",
  skipped: "Skipped",
  stale: "Superseded",
};

export function stateLabel(state) {
  return STATE_LABEL[state] ?? state;
}

export function StateDot({ state }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "8px",
        font: "var(--text-caption)",
        color: "var(--text-secondary)",
        whiteSpace: "nowrap",
      }}
    >
      <span
        aria-hidden="true"
        style={{
          width: "6px",
          height: "6px",
          borderRadius: "999rem",
          background: STATE_TONE[state] ?? "var(--text-muted)",
        }}
      />
      {stateLabel(state)}
    </span>
  );
}

/* Cells of one panel divided by a hairline rather than four separate cards: the
   prototype's KPI strip is a single surface. */
export function KPI({ label, value, delta, index = 0 }) {
  return (
    <div
      style={{
        padding: "18px 20px",
        borderLeft: index ? "1px solid var(--surface-elevated)" : "none",
        animation: `cn-fade-up 460ms var(--ease-out) ${index * 80}ms both`,
      }}
    >
      <span
        style={{
          display: "block",
          font: "var(--text-body-14)",
          color: "var(--text-muted)",
          marginBottom: "10px",
        }}
      >
        {label}
      </span>
      <span style={{ display: "flex", alignItems: "baseline", gap: "10px" }}>
        <span
          style={{
            font: "500 clamp(22px,2.2vw,30px)/1 var(--font-display)",
            letterSpacing: "-0.03em",
            color: "var(--text-primary)",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {value ?? "—"}
        </span>
        {delta !== null && delta !== undefined && (
          <span
            style={{
              font: "10px/1 var(--font-mono)",
              color: "var(--danger)",
              background: "var(--danger-surface)",
              border: "1px solid var(--danger-border)",
              padding: "4px 7px",
              borderRadius: "5px",
            }}
          >
            {delta >= 0 ? "+" : ""}
            {delta}%
          </span>
        )}
      </span>
    </div>
  );
}

export const KPI_STRIP = {
  ...panel,
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit,minmax(190px,1fr))",
};

/* The prototype's chart: a rotated mono axis label, three gridlines with mono ticks, and
   a day scale beneath. It draws a flat line because it has no data; this draws the series
   it is given and keeps the flat baseline for the case where there is still none. */
export function LineChart({ series, valueKey, label, height = 132 }) {
  const points = series ?? [];
  const values = points.map((point) => Number(point[valueKey] ?? 0));
  const peak = Math.max(1, ...values);
  const [left, right, top, floor] = [14, 300, 10, 110];

  const coords = values.map((value, index) => {
    const x =
      left +
      (values.length === 1
        ? (right - left) / 2
        : (index / (values.length - 1)) * (right - left));
    const y = floor - (value / peak) * (floor - top);
    return `${x},${y}`;
  });

  const ticks = [
    [top, String(peak)],
    [(top + floor) / 2, String(Math.round(peak / 2))],
    [floor, "0"],
  ];

  return (
    <div style={{ position: "relative", padding: "18px 18px 8px" }}>
      <div style={{ display: "grid", gridTemplateColumns: "22px 1fr", gap: "10px" }}>
        <span
          style={{
            writingMode: "vertical-rl",
            transform: "rotate(180deg)",
            justifySelf: "center",
            alignSelf: "center",
            font: "9px/1 var(--font-mono)",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "var(--text-disabled)",
          }}
        >
          {label}
        </span>
        <div>
          <svg
            viewBox="0 0 300 120"
            preserveAspectRatio="none"
            role="img"
            aria-label={`${label}: ${values.reduce((sum, value) => sum + value, 0)} total`}
            style={{ width: "100%", height: `${height}px`, display: "block" }}
          >
            {ticks.map(([y, text]) => (
              <g key={text + y}>
                <line x1={left} y1={y} x2={right} y2={y} stroke="var(--border)" strokeWidth="0.7" />
                <text x="0" y={y + 3} fill="var(--text-muted)" style={{ font: "7px var(--font-mono)" }}>
                  {text}
                </text>
              </g>
            ))}
            {coords.length > 1 ? (
              <polyline
                points={coords.join(" ")}
                fill="none"
                stroke="var(--graph-line)"
                strokeWidth="1.4"
                strokeLinejoin="round"
                strokeLinecap="round"
                vectorEffect="non-scaling-stroke"
              />
            ) : (
              <line
                x1={left}
                y1={floor}
                x2={right}
                y2={floor}
                stroke="var(--text-faint)"
                strokeWidth="1.2"
                opacity="0.9"
              />
            )}
          </svg>
          <div style={{ display: "flex", justifyContent: "space-between", paddingLeft: "14px" }}>
            {axisDays(points).map((tick, index) => (
              <span
                key={tick.key}
                style={{
                  font: "9px/1.4 var(--font-mono)",
                  color: "var(--text-muted)",
                  textAlign: "center",
                }}
              >
                {tick.day}
                {index === 0 && <span style={{ display: "block" }}>{tick.month}</span>}
              </span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* Ten evenly spaced labels at most: past that the scale reads as a smear. */
function axisDays(points) {
  if (!points.length) return [];
  const step = Math.max(1, Math.ceil(points.length / 10));
  const picked = points.filter((_, index) => index % step === 0).slice(0, 10);
  return picked.map((point) => {
    const date = new Date(point.day ?? point.date ?? point.bucket ?? Date.now());
    return {
      key: String(point.day ?? point.date ?? point.bucket ?? Math.random()),
      day: date.getUTCDate(),
      month: date.toLocaleString(undefined, { month: "short", timeZone: "UTC" }).toUpperCase(),
    };
  });
}

/* ----------------------------------------------------------------------------- table */

const TH = {
  font: "var(--text-caption)",
  fontFamily: "var(--font-mono)",
  letterSpacing: "0.07em",
  textTransform: "uppercase",
  color: "var(--text-muted)",
  padding: "11px 16px",
  textAlign: "left",
  whiteSpace: "nowrap",
};

const TD = {
  font: "var(--text-body-14)",
  color: "var(--text-secondary)",
  padding: "14px 16px",
  borderTop: "1px solid var(--surface-elevated)",
  verticalAlign: "top",
};

export function Table({ columns, rows, empty, onRowClick, minWidth = "660px" }) {
  if (!rows.length) return <Empty>{empty}</Empty>;
  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", minWidth }}>
        <thead>
          <tr style={{ background: "var(--surface-elevated)" }}>
            {columns.map((col) => (
              <th key={col.key} style={{ ...TH, textAlign: col.align ?? "left" }}>
                {col.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              key={row.id ?? index}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={{
                cursor: onRowClick ? "pointer" : "default",
                opacity: row.dimmed ? 0.5 : 1,
                transition: "opacity 260ms var(--ease-out)",
                animation: `cn-fade-up 420ms var(--ease-out) ${index * 60}ms both`,
              }}
            >
              {columns.map((col) => (
                <td
                  key={col.key}
                  style={{
                    ...TD,
                    textAlign: col.align ?? "left",
                    ...(col.nowrap ? { whiteSpace: "nowrap" } : null),
                  }}
                >
                  {col.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* The prototype's table footer: a count on the left, two buttons on the right, inside
   the panel rather than floating below it. */
export function Pager({ page, onChange }) {
  if (!page || page.total <= page.limit) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "12px 16px",
        borderTop: "1px solid var(--surface-elevated)",
      }}
    >
      <span style={{ font: "var(--text-caption)", color: "var(--text-muted)" }}>
        Showing {page.offset + 1}–{page.offset + page.items.length} of {page.total}
      </span>
      <span style={{ marginLeft: "auto", display: "flex", gap: "7px" }}>
        <PagerButton
          disabled={page.offset === 0}
          onClick={() => onChange(Math.max(0, page.offset - page.limit))}
        >
          Previous
        </PagerButton>
        <PagerButton disabled={!page.hasMore} onClick={() => onChange(page.offset + page.limit)}>
          Next
        </PagerButton>
      </span>
    </div>
  );
}

function PagerButton({ disabled, onClick, children }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      style={{
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.4 : 1,
        font: "var(--text-caption)",
        color: "var(--text-secondary)",
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "8px",
        padding: "7px 12px",
      }}
    >
      {children}
    </button>
  );
}

/* ---------------------------------------------------------------------- panel states */

/* A dashed well, inset from the panel's edges. The prototype uses it for every "nothing
   here yet", which is most of its dashboard. */
export function Empty({ children }) {
  return (
    <div
      style={{
        position: "relative",
        display: "grid",
        placeItems: "center",
        flex: "1 1 auto",
        minHeight: "104px",
        margin: "14px 18px 18px",
        borderRadius: "10px",
        border: "1px dashed var(--border)",
        overflow: "hidden",
        font: "var(--text-body-14)",
        color: "var(--text-muted)",
        textAlign: "center",
        padding: "0 12px",
      }}
    >
      <span style={{ position: "relative" }}>{children}</span>
    </div>
  );
}

export function Loading({ label = "Loading…" }) {
  return <Empty>{label}</Empty>;
}

/* A failed panel says what failed and offers the retry, rather than rendering an empty
   state that reads as "you have no data". The two are not the same and look identical
   unless the error is shown. */
export function Failed({ error, onRetry }) {
  return (
    <div style={{ padding: "28px 18px", textAlign: "center" }}>
      <p style={{ font: "var(--text-body-14)", color: "var(--danger)", margin: "0 0 12px" }}>
        {error?.message ?? "Something went wrong."}
      </p>
      {onRetry && (
        <Button tone="secondary" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

/* A panel's three states in one place, so no screen renders data and a spinner at once. */
export function Async({ state, children, empty }) {
  if (state.loading && !state.data) return <Loading />;
  if (state.error) return <Failed error={state.error} onRetry={state.reload} />;
  if (!state.data) return <Empty>{empty ?? "No data available"}</Empty>;
  return children(state.data);
}

/* ------------------------------------------------------------------------ formatting */

export function relativeTime(iso) {
  if (!iso) return "—";
  const seconds = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  const units = [
    ["year", 31_536_000],
    ["month", 2_592_000],
    ["day", 86_400],
    ["hour", 3_600],
    ["minute", 60],
  ];
  const formatter = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" });
  for (const [unit, size] of units) {
    if (Math.abs(seconds) >= size) return formatter.format(-Math.round(seconds / size), unit);
  }
  return formatter.format(-seconds, "second");
}

export function duration(ms) {
  if (ms === null || ms === undefined) return "—";
  const minutes = ms / 60_000;
  if (minutes < 1) return `${Math.round(ms / 1000)}s`;
  if (minutes < 60) return `${Math.round(minutes)}m`;
  const hours = minutes / 60;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  return `${(hours / 24).toFixed(1)}d`;
}
